/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import Msdyn365 from '@msdyn365-commerce/core';

import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import { IWebgroupCategoriesData } from './webgroup-categories.data';
import { IWebgroupCategoriesProps } from './webgroup-categories.props.autogenerated';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { GetCategoryIdFromUrl } from '../../utilities';

export interface IWebgroupCategoriesViewProps extends IWebgroupCategoriesProps<IWebgroupCategoriesData> {
    categories?: React.ReactNode;
    categoryHierarchyList?: any;
}
export interface CategoryHierarchyExtended extends CategoryHierarchy {
    selected?: boolean;
    depth?: number | string | undefined;
}

export interface IWebgroupCategoriesState {
    categoryId: number;
    categories: CategoryHierarchyExtended[];
    webgroup: CategoryHierarchyExtended | null;
}

/**
 *
 * WebgroupCategories component
 * @extends {React.PureComponent<IWebgroupCategoriesProps<IWebgroupCategoriesData>>}
 */
class WebgroupCategories extends React.PureComponent<IWebgroupCategoriesViewProps, IWebgroupCategoriesState> {
    public constructor(props: IWebgroupCategoriesViewProps) {
        super(props);
        this.state = {
            categoryId: 0,
            categories: [],
            webgroup: null
        };
    }
    private _renderLink = (category: any): React.ReactNode => {
        const {
            context: { request }
        } = this.props;
        if (Msdyn365.isBrowser && category) {
            const url = new URL(`${request.url.requestUrl.origin}/${this.props.config.pageName}`);
            url.searchParams.append('categoryId', category.RecordId);
            return <a href={url.href}>{category.Name?.replace(/\s?\(\d*\)$/, '')}</a>;
        }
        return <></>;
    };
    private _renderChildCategoy = (category: any | null, first: boolean = false): React.ReactNode => {
        let showChildren = false;
        const checkChildren = (category: CategoryHierarchy): void => {
            for (const cat of category.Children) {
                if (cat.RecordId === this.state.categoryId) {
                    showChildren = true;
                    break;
                } else if (cat.Children) {
                    checkChildren(cat);
                }
            }
        };
        const generateClasses = (): string => {
            const classes = [
                'sub-category',
                first ? 'first' : '',
                category.selected ? 'selected' : '',
                showChildren ? 'show-children' : ''
            ];
            return classes.join(' ');
        };
        category.Children && checkChildren(category);
        return (
            <div key={category.RecordId} className={generateClasses()}>
                {this._renderLink(category)}
                {category.Children?.length ? category.Children.map((c: any) => this._renderChildCategoy(c)) : null}
            </div>
        );
    };

    private _renderCategories = (): React.ReactNode => {
        if (this.state.categories.length) {
            // list category hierarchy in category landing page
            // Get the top level category hierarchy
            const filterdCategory = this.state.categoryId
                ? this.findCategoryHierarchy(this.state.categories[0].Children, this.state.categoryId)?.pop()
                : null;
            if (filterdCategory) {
                if (this.state.webgroup) {
                    return (
                        <div key={this.state.webgroup?.Name}>
                            <h3 className='category-title'>{this._renderLink(this.state.webgroup)}</h3>
                            {this.state.webgroup?.Children?.length
                                ? this.state.webgroup?.Children.map((c: any) => this._renderChildCategoy(c, true))
                                : null}
                        </div>
                    );
                }
                return (
                    <div key={filterdCategory.Name}>
                        <h3 className='category-title'>{this._renderLink(filterdCategory)}</h3>
                        {filterdCategory.Children?.length
                            ? filterdCategory.Children.map((c: any) => this._renderChildCategoy(c, true))
                            : null}
                    </div>
                );
            } else {
                // list top level categories on search results page
                return this.state.categories.map((cat: CategoryHierarchy) => {
                    return (
                        <div key={cat.Name}>
                            {cat.Children?.length ? cat.Children.map((c: any) => this._renderChildCategoy(c, true)) : null}
                        </div>
                    );
                });
            }
        }
        return <></>;
    };
    private setWebGroup = (categories: CategoryHierarchyExtended[], categoryId: number | undefined): void => {
        for (let i = 0; i < categories.length; i++) {
            if (categories[`${i}`].RecordId === categoryId) {
                this.setState({ webgroup: categories[`${i}`] });
                break;
            }
            if (!this.state.webgroup && ArrayExtensions.hasElements(categories[`${i}`].Children)) {
                this.setWebGroup(categories[`${i}`].Children, categoryId);
            }
        }
    };
    private findCategoryHierarchy = (categories: CategoryHierarchyExtended[], categoryId: number): CategoryHierarchyExtended[] | null => {
        for (const category of categories) {
            const depth = category.Slug && [...category.Slug.matchAll(new RegExp('/', 'gi'))].map(i => i.index);
            category.depth = depth && depth.length - 2;
            if (category.RecordId === categoryId) {
                category.selected = true;
                if (category.depth && +category.depth > 3) {
                    this.setWebGroup(this.state.categories[0].Children, category.ParentCategory);
                }
                return [category];
            }
            if (ArrayExtensions.hasElements(category.Children)) {
                const innerCategoryHierarchy = this.findCategoryHierarchy(category.Children, categoryId);
                if (ArrayExtensions.hasElements(innerCategoryHierarchy)) {
                    innerCategoryHierarchy.push(category);
                    return innerCategoryHierarchy;
                }
            }
        }
        return null;
    };
    public render(): JSX.Element | null {
        const {
            data: { categories }
        } = this.props;
        const categoryId = GetCategoryIdFromUrl(this.props.context.actionContext);
        this.setState({ categoryId });
        this.setState({ categories: categories?.result || [] });
        const viewProps = {
            ...this.props,
            categories: this._renderCategories()
        };
        return this.props.renderView(viewProps);
    }
}

export default WebgroupCategories;
